import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import api from '../api/client';
import { inlineError } from '../hooks/useApiCall';
import { usePageTitle } from '../hooks/usePageTitle';
import { HourglassIcon, CheckIcon, XMarkIcon } from '../components/shared/Icons';
import AuthCard from '../components/auth/AuthCard';
import AuthResult from '../components/auth/AuthResult';

export default function VerifyEmail() {
  const { t } = useI18n();
  usePageTitle(t.auth.checkEmail);
  const [params] = useSearchParams();
  const token = params.get('token');
  // The body mounts again for each token: a link with a new token starts at "verifying",
  // and the answer of the old token cannot draw over it.
  return <VerifyEmailBody key={token ?? ''} token={token} />;
}

function VerifyEmailBody({ token }: { token: string | null }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // No token: nothing to send. The text is not stored, so it follows the language of the page.
    if (!token) return;
    let live = true;

    // The link verifies the address and signs nobody in (#447): a signed-in
    // link let an attacker put a victim into the attacker's account.
    api.get('/api/auth/email/verify', { params: { token } })
      .then(() => { if (live) setStatus('success'); })
      .catch(err => {
        if (!live) return;
        setStatus('error');
        setErrorMsg(inlineError(err, t.auth.verificationFailed));
      });
    return () => { live = false; };
  }, [token]);

  const shown = token ? status : 'error';
  return (
    <AuthCard>
      {shown === 'verifying' && <AuthResult icon={HourglassIcon} pulse title={t.common.loading} />}
      {shown === 'success' && (
        <AuthResult icon={CheckIcon} tone="success" title={t.auth.emailVerified} text={t.auth.canNowLogin}
          action={{ to: '/login', label: t.nav.login }} />
      )}
      {shown === 'error' && (
        <AuthResult icon={XMarkIcon} tone="error" title={t.auth.verificationFailed}
          text={token ? errorMsg : t.auth.noVerificationToken}
          action={{ to: '/login', label: t.auth.backToLogin, variant: 'outline' }} />
      )}
    </AuthCard>
  );
}
